import { supabase } from '../../shared/supabase/supabaseClient';
import {
  isCategoryValidForDirection,
  type CashMovementCategory,
  type CashMovementDirection,
} from './cashOutflowCategories';

export type CashOutflowRow = {
  id: string;
  branchId: string;
  cashSessionId: string;
  /** 'out' egreso (default server) | 'in' ingreso manual. */
  direction: CashMovementDirection;
  category: CashMovementCategory;
  amount: number;
  note: string | null;
  createdAt: string;
  createdBy: string | null;
  status: 'active' | 'voided';
  voidedAt: string | null;
  voidedBy: string | null;
  voidReason: string | null;
};

function mapRow(row: Record<string, unknown>): CashOutflowRow {
  const direction: CashMovementDirection = row.direction === 'in' ? 'in' : 'out';
  const category = String(row.category ?? '');
  if (!isCategoryValidForDirection(direction, category)) {
    throw new Error(`Categoría inválida para ${direction === 'in' ? 'ingreso' : 'egreso'}: ${category}`);
  }
  return {
    id: String(row.id),
    branchId: String(row.branch_id),
    cashSessionId: String(row.cash_session_id),
    direction,
    category,
    amount: Number(row.amount ?? 0),
    note: row.note != null ? String(row.note) : null,
    createdAt: String(row.created_at ?? ''),
    createdBy: row.created_by != null ? String(row.created_by) : null,
    status: row.status === 'voided' ? 'voided' : 'active',
    voidedAt: row.voided_at != null ? String(row.voided_at) : null,
    voidedBy: row.voided_by != null ? String(row.voided_by) : null,
    voidReason: row.void_reason != null ? String(row.void_reason) : null,
  };
}

/**
 * Fetch remoto de egresos de una sesion via RPC `reports_cash_session_outflows`.
 * Retorna null si hay error de red o RPC (caller debe hacer fallback local).
 * P1 #850: localDb stub retorna [] — este fetch es el path principal en produccion.
 */
export async function fetchRemoteOutflowsForSession(sessionId: string): Promise<CashOutflowRow[] | null> {
  const { data, error } = await supabase.rpc('reports_cash_session_outflows', {
    p_session_id: sessionId,
  });
  if (error) {
    console.warn('[cashOutflowRepo] reports_cash_session_outflows', error.message);
    return null;
  }
  if (!Array.isArray(data)) return null;
  const out: CashOutflowRow[] = [];
  for (const row of data as Record<string, unknown>[]) {
    try {
      out.push(mapRow(row));
    } catch {
      // skip rows with invalid category
    }
  }
  return out;
}

/**
 * Lista egresos activos de una sesion via RPC remoto.
 * ADR-017: localDb stub eliminado — sin fallback local.
 */
export async function listActiveOutflowsForSessionRemoteFirst(sessionId: string): Promise<CashOutflowRow[]> {
  const remote = await fetchRemoteOutflowsForSession(sessionId);
  if (remote != null) {
    return remote.filter((r) => r.status === 'active');
  }
  return [];
}

/**
 * Lista todos los egresos de una sesion (activos + anulados) via RPC remoto.
 * Usado por useCashSessionMovements para construir el feed de actividad.
 */
export async function listOutflowsForSessionRemoteFirst(sessionId: string): Promise<CashOutflowRow[]> {
  const remote = await fetchRemoteOutflowsForSession(sessionId);
  if (remote != null) return remote;
  return [];
}
